import { Steps } from 'antd';
import { useReducer, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useGenerateAuthenticatorSetupQuery } from '../../../../api/queries/generateAuthenticatorSetup';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import breakpoints from '../../../../utilities/breakpoints';
import CodeStep from './CodeStep';
import QRCodeStep from './QRCodeStep';

const { Step } = Steps;

type State = { step: 'scan' } | { step: 'verification' };

type Action = { type: 'goToScan' } | { type: 'goToVerifiy' };

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'goToScan':
            return { step: 'scan' };

        case 'goToVerifiy':
            return { step: 'verification' };

        default:
            return state;
    }
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 24px 24px;
    max-width: 30vw;

    @media screen and (max-width: ${breakpoints.lg}) {
        max-width: 70vw;
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        max-width: 90vw;
    }

    & button {
        flex: 1;
        font-size: 16px;
        min-width: 100px;
        width: 100%;
        height: 48px;
    }

    & .ant-steps-vertical {
        margin-bottom: 16px;
    }
`;

type AuthenticatorContentProps = {
    onDone: () => void;
};
const AuthenticatorContent = ({ onDone }: AuthenticatorContentProps) => {
    const { t } = useTranslation('userSelf');
    const [state, dispatch] = useReducer(reducer, { step: 'scan' });

    const actions = useMemo(
        () => ({
            goToScan: () => dispatch({ type: 'goToScan' }),
            goToVerify: () => dispatch({ type: 'goToVerifiy' }),
        }),
        [dispatch]
    );

    const { data, loading } = useGenerateAuthenticatorSetupQuery({ fetchPolicy: 'no-cache' });

    if (loading) {
        return <PortalLoadingElement />;
    }

    if (!data) {
        return null;
    }

    const body =
        state.step === 'scan' ? (
            <QRCodeStep next={actions.goToVerify} qrcodeUri={data.response.qrcodeUri} />
        ) : (
            <CodeStep next={onDone} previous={actions.goToScan} secret={data.response.secret} />
        );

    return (
        <Container>
            <Steps current={state.step === 'scan' ? 0 : 1} direction="vertical">
                <Step style={{ height: '70px' }} title={t('userSelf:authenticatorSettings.setup.qrcodeStep.title')} />
                <Step description=" " title={t('userSelf:authenticatorSettings.setup.codeStep.title')} />
            </Steps>
            {body}
        </Container>
    );
};

export default AuthenticatorContent;
